import moneyMask from '@eduzz/houston-forms/masks/money';
import Alert from '@eduzz/houston-ui/Alert';
import Input from '@eduzz/houston-ui/Forms/Input';
import Select from '@eduzz/houston-ui/Forms/Select';
import Link from '@material-ui/core/Link';
import Tooltip from '@material-ui/core/Tooltip';
import CancelIcon from '@material-ui/icons/Cancel';
import HelpIcon from '@material-ui/icons/Help';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { numberToBRL } from 'v2/helpers/money';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';
import { transferService } from 'v2/services/transfer';
import * as Yup from 'yup';

import styles from '../styles';
import { MIN_TRANSFER, MIN_TRANSFER_INTERNATIONAL, LIMIT_TRANSFER_PF } from './contants';

import type { TransferByInvoice } from '../../constants';

type selectOption = {
  label: string;
  value: string;
};

type Props = {
  loading: boolean;
  formIsValid: boolean;
  selectedBank: number;
  accountBankList: selectOption[];
  setSelectedBank: React.Dispatch<React.SetStateAction<number>>;
  valueToTransfer: string | null;
  setValueToTransfer: React.Dispatch<React.SetStateAction<string>>;
  setFormIsValid: Function;
  transferByInvoiceObj: TransferByInvoice;
  resetTransferByInvoiceObj: Function;
};

function TransferStep({
  loading,
  formIsValid,
  selectedBank,
  accountBankList,
  setSelectedBank,
  valueToTransfer,
  setValueToTransfer,
  setFormIsValid,
  transferByInvoiceObj,
  resetTransferByInvoiceObj,
}: Props) {
  const classes = styles();

  const [formMessageError, setFormMessageError] = useState(null);
  const [limitToTransfer, setLimitToTransfer] = useState(null);

  const [currentBalance] = useObservable(() => financialStatementService.getCurrentBalance(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const [getLimitAvailableToTransfer] = useCallbackObservable(
    (currUser) =>
      transferService.getAvailablenarturalPerson().pipe(
        tap(({ data }) => {
          setLimitToTransfer(currUser.crossborder ? data.total_origin : data.total);
        }),
        catchError((error) => {
          toast.error(error);
          return of(true);
        })
      ),
    []
  );

  const minTransferFormatted = numberToBRL(user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER);
  const balanceFormatted = typeof currentBalance === 'number' ? numberToBRL(currentBalance) : '-';
  const limitFormatted = typeof limitToTransfer === 'number' ? numberToBRL(limitToTransfer) : '-';

  const validationMinValue = user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;
  const validationMaxValue = user?.type === 'F' ? LIMIT_TRANSFER_PF : currentBalance;
  const validationMaxValueFormatted = user?.type === 'F' ? limitFormatted : balanceFormatted;

  const validateValue = async (value: string) => {
    const transferSchema = Yup.object().shape({
      valueToTransfer: Yup.number()
        .required()
        .min(validationMinValue, `Valor mínimo: ${minTransferFormatted}`)
        .max(validationMaxValue, `Valor máximo: ${validationMaxValueFormatted}`),
    });

    try {
      await transferSchema.validate({ valueToTransfer: value });
      setFormIsValid(true);
      setFormMessageError(null);
    } catch (err) {
      setFormIsValid(false);
      setFormMessageError(err.message);
    }
  };

  const handleInputChange = (value: string) => {
    resetTransferByInvoiceObj();
    validateValue(value);
    setValueToTransfer(value);
  };

  const handleInputReset = () => {
    setValueToTransfer('');
    validateValue('0');
  };

  const getTransferByInvoiceMessage = () => {
    const transferByInvoiceFormatted = numberToBRL(transferByInvoiceObj.approx_amount);

    if (transferByInvoiceObj.approx_amount < validationMinValue) {
      return (
        <>
          <div>{`O valor mínimo para saque em nossa plataforma é de ${minTransferFormatted} e a soma de faturas com valor mais próximo ao solicitado é de ${transferByInvoiceFormatted}.`}</div>
          <div>Tente novamente com um valor acima disso e dará tudo certo</div>
        </>
      );
    }

    return `A soma das faturas com valor mais próximo ao solicitado é de ${transferByInvoiceFormatted}. Clique em "Transferir valor" para prosseguir com o valor apurado.`;
  };

  useEffect(
    () => setValueToTransfer(user?.type === 'F' ? limitToTransfer : currentBalance),
    [currentBalance, limitToTransfer, setValueToTransfer, user?.type]
  );
  useEffect(() => user?.type === 'F' && getLimitAvailableToTransfer(user), [getLimitAvailableToTransfer, user]);

  return (
    <>
      <p className={classes.subtitle}>Informe o valor que deseja transferir para sua conta bancária.</p>

      <div className={classes.valueAvailable}>
        <span>Disponível para transferência:</span>
        <strong>{validationMaxValueFormatted}</strong>
      </div>

      <div className={classes.selectValueAvailable}>
        <span>
          Valor que deseja transferir:
          <Tooltip title='Será debitado o valor de R$ 9,00 do valor transferido devido encargos bancários.' placement='top'>
            <HelpIcon className={classes.tooltipIcon} />
          </Tooltip>
        </span>
        <Input
          className={user?.type === 'F' && limitToTransfer < LIMIT_TRANSFER_PF ? classes.inputValueWarning : classes.inputValue}
          name='valueToTransfer'
          placeholder='R$ 0,00'
          disabled={typeof valueToTransfer === null || loading}
          value={valueToTransfer}
          onChange={handleInputChange}
          mask={moneyMask}
          errorMessage={!formIsValid && formMessageError}
          endAdornment={<CancelIcon className={classes.clearValue} onClick={handleInputReset} />}
        />
      </div>

      {user?.type === 'F' && typeof limitToTransfer === 'number' && !!accountBankList && (
        <div className={classes.alertList}>
          <Alert className={classes.alertItem} closeIcon={false} type={limitToTransfer < LIMIT_TRANSFER_PF ? 'warning' : 'informative'}>
            <p className={classes.alertText}>
              A sua conta, por ser de Pessoa Física, possui limite de transferência mensal.{' '}
              <Link component={RouterLink} to='/producer/financial-statement/transfer-history' target='_blank'>
                <strong>Clique aqui e confira suas transferências já realizadas!</strong>
                <br />
              </Link>
            </p>

            <p className={classes.alertText}>
              <span>{`O seu limite atual é de ${limitFormatted}. `} </span>
              <Link
                target='_blank'
                rel='noopener noreferrer'
                href='https://ajuda.eduzz.com/hc/pt-br/articles/4402979256859-Limite-de-saque-de-R-1-900-00-para-pessoa-f%C3%ADsica-por-qu%C3%AA-'
              >
                <strong>Saiba mais!</strong>
              </Link>
            </p>
          </Alert>
        </div>
      )}

      {!!transferByInvoiceObj && (
        <Alert type='warning' closeIcon={false}>
          {getTransferByInvoiceMessage()}
        </Alert>
      )}

      <div className={classes.selectAccount}>
        {!!accountBankList && (
          <>
            <label className={classes.selectLabel}>Conta bancária</label>
            <Select name='accountBanklist' value={selectedBank} onChange={setSelectedBank} disabled={loading || !accountBankList} fullWidth>
              {accountBankList?.map((option, index) => (
                <Select.Option key={`${option.value}-${index}`} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </>
        )}
        {!accountBankList && (
          <Alert type='warning' closeIcon={false}>
            Falta pouco! Como você não tem nenhuma conta bancária ativa no momento,{' '}
            <Link component={RouterLink} to='/producer/profile/bankdata'>
              <strong>clique aqui e cadastre agora!</strong>
            </Link>
          </Alert>
        )}
      </div>
    </>
  );
}

export default TransferStep;
